
import { useState, useEffect } from "react";
import { Popover } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { FilePenLine } from "lucide-react";

import { showToast } from "utils/utility";
import { useHotelData } from "libs/data-access";
import { IHotelOnlineBookingList } from "libs/data-access/types";
import { EButton, ETooltip, ENumberInput, ETextArea } from "libs/ui";

interface EditRefundChargesProps {
    hotelTicket: IHotelOnlineBookingList;
    handleRefresh: () => void;
};

export default function EditHotelRefundCharges({ hotelTicket, handleRefresh }: EditRefundChargesProps) {

    const { postHotelData } = useHotelData();

    const [opened, { open, close }] = useDisclosure(false);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedSegment, setSelectedSegment] = useState<string>("0");
    const [refundCharges, setRefundCharges] = useState<{
        supplierCharge: number;
        cancellationCharge: number;
        bookingId: number;
        remark: string;
    }>({
        supplierCharge: 0,
        cancellationCharge: 0,
        bookingId: 0,
        remark: ""
    });


    // Initialize refund charges from props
    useEffect(() => {
        if (hotelTicket) {
            const initialCharges = {
                supplierCharge: hotelTicket.cancellationChargeResponse?.supplierCharge || 0,
                cancellationCharge: hotelTicket.cancellationChargeResponse?.cancellationCharge || 0,
                bookingId: hotelTicket.id,
                remark: ""
            };
            setRefundCharges(initialCharges);
        }
    }, [hotelTicket]);


    // HANDLER FUNCTIONS
    const handleChargeChange = (
        field: 'supplierCharge' | 'cancellationCharge' | 'remark',
        value: number | string
    ) => {
        const updatedCharges = { ...refundCharges };
        updatedCharges[field] = value as never;
        setRefundCharges(updatedCharges);
    };


    // API CALLS
    const handleSaveCharges = async () => {
        if (!hotelTicket.id) {
            showToast({
                type: "error",
                message: "Booking ID not found!"
            });
            return;
        };

        const selectedCharge = refundCharges;
        if (!selectedCharge?.bookingId) {
            showToast({
                type: "error",
                message: "Selected segment booking ID not found!"
            });
            return;
        };

        setIsLoading(true);

        try {
            const requestData = {
                bookingId: selectedCharge.bookingId,
                supplierCharge: selectedCharge.supplierCharge,
                cancellationCharge: selectedCharge.cancellationCharge,
                remark: selectedCharge.remark
            };

            const response = await postHotelData(
                `/booking/${requestData.bookingId}/change-cancellation-charges?supplierCharge=${requestData.supplierCharge}&cancellationCharge=${requestData.cancellationCharge}&remark=${requestData.remark}`,
                null
            );

            if (response.isSuccess) {
                showToast({
                    type: "success",
                    message: `Refund charges updated successfully for segment ${Number(selectedSegment) + 1}!`
                });
                close();
                handleRefresh();
            } else {
                console.log("Error while updating refund charges", response);
                showToast({
                    type: "error",
                    message: response.message || "Failed to update refund charges!"
                });
            };
        } catch (error) {
            console.error("Error updating refund charges:", error);
            showToast({
                type: "error",
                message: "An error occurred while updating refund charges."
            });
        } finally {
            setIsLoading(false);
        };
    };

    // ... rest of the code remains unchanged ...

    // const handleSaveCharges = async () => {
    //   if (!flightTicket.uuid) {
    //     showToast({
    //       type: "error",
    //       message: "Booking ID not found!"
    //     });
    //     return;
    //   }

    //   setIsLoading(true);

    //   try {
    //     const requestData = refundCharges.map(charge => ({
    //       bookingId: charge.bookingId,
    //       supplierCharge: charge.supplierCharge,
    //       cancellationCharge: charge.cancellationCharge
    //     }));

    //     // console.log(requestData);

    //     const response = await postFlightData(
    //         `/booking/${flightTicket.uuid}/change-cancellation-charges`,
    //         requestData
    //     );

    //     if (response.isSuccess) {
    //         showToast({
    //             type: "success",
    //             message: "Refund charges updated successfully!"
    //         });
    //         close();
    //         handleRefresh();
    //     } else {
    //         showToast({
    //             type: "error",
    //             message: response.message || "Failed to update refund charges!"
    //         });
    //     };
    //   } catch (error) {
    //     console.error("Error updating refund charges:", error);
    //     showToast({
    //       type: "error",
    //       message: "An error occurred while updating refund charges."
    //     });
    //   } finally {
    //     setIsLoading(false);
    //   }
    // };


    return (
        <Popover
            position="bottom-end"
            withArrow
            shadow="md"
            opened={opened}
            onChange={open}
        >
            <Popover.Target>
                <div>
                    <ETooltip label="Edit Refund Charges">
                        <EButton
                            size="sm"
                            variant="light"
                            color="blue"
                            onClick={open}
                        >
                            <FilePenLine size={20} className="mr-1" />
                            <span className="hidden sm:inline">Edit Refund Charges</span>
                        </EButton>
                    </ETooltip>
                </div>
            </Popover.Target>

            <Popover.Dropdown>
                <div className="w-96 p-2">
                    <h3 className="text-lg font-medium mb-4">Edit Refund Charges</h3>
                    {/* Selected Segment Charges */}
                    {refundCharges && (
                        <div className="mb-4">
                            <div className="grid grid-cols-1 gap-4">
                                <div>
                                    <label className="text-sm text-gray-600">Supplier Charge</label>
                                    <ENumberInput
                                        value={refundCharges?.supplierCharge}
                                        onChange={(value) => handleChargeChange('supplierCharge', Number(value))}
                                        min={0}
                                        // precision={2}
                                        className="mt-1"
                                    />
                                </div>

                                <div>
                                    <label className="text-sm text-gray-600">Cancellation Charge</label>
                                    <ENumberInput
                                        value={refundCharges?.cancellationCharge}
                                        onChange={(value) => handleChargeChange('cancellationCharge', Number(value))}
                                        min={0}
                                        // precision={2}
                                        className="mt-1"
                                    />
                                </div>

                                <div>
                                    <label className="text-sm text-gray-600">Remark</label>
                                    <ETextArea
                                        value={refundCharges?.remark}
                                        onChange={(e) => handleChargeChange('remark', e.target.value)}
                                        className="mt-1"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-end gap-2 mt-4">
                        <EButton
                            size="sm"
                            variant="subtle"
                            onClick={close}
                            disabled={isLoading}
                        >
                            Cancel
                        </EButton>

                        <EButton
                            size="sm"
                            color="blue"
                            onClick={handleSaveCharges}
                            loading={isLoading}
                        >
                            Save Changes
                        </EButton>
                    </div>
                </div>
            </Popover.Dropdown>
        </Popover>
    );
} 